import { HolderOutlined } from '@ant-design/icons';
import { DragEvent, useState } from 'react';

import ChevronRight from 'assets/svg/ChevronRight';
import User from 'assets/svg/User';
import { colorConstants } from 'config/colorConstants';

import { sortLensesByDisplayOrder } from './scenarioLensOrder.helpers';
import type { ScenarioViewLens } from './scenarioLens.types';
import {
    StyledLensExpandButton,
    StyledLensSettingsItem,
    StyledLensSettingsList,
} from './ScenarioLensSection.styles';

type LensSettingsSortableListProps = {
    lenses: ScenarioViewLens[];
    displayLenses?: ScenarioViewLens[];
    onChange: (lenses: ScenarioViewLens[]) => void;
    sortable?: boolean;
    showDragHandle?: boolean;
    expandedLensId?: number | null;
    onExpandLens?: (lensId: number) => void;
};

const reorderLensList = (lenses: ScenarioViewLens[], sourceLensId: number, targetLensId: number) => {
    if (sourceLensId === targetLensId) {
        return lenses;
    }

    const orderedLenses = sortLensesByDisplayOrder(lenses);
    const from = orderedLenses.findIndex((lens) => lens.lens_id === sourceLensId);
    const to = orderedLenses.findIndex((lens) => lens.lens_id === targetLensId);
    if (from === -1 || to === -1) {
        return lenses;
    }

    const nextLenses = [...orderedLenses];
    const [movedLens] = nextLenses.splice(from, 1);
    nextLenses.splice(to, 0, movedLens);
    // Assign display_order based on the new array order directly — do NOT sort again,
    // as that would revert the splice back to the original order.
    return nextLenses.map((lens, index) => ({ ...lens, display_order: index + 1 }));
};

const LensSettingsSortableList = ({
    lenses,
    displayLenses,
    onChange,
    sortable = true,
    showDragHandle = true,
    expandedLensId = null,
    onExpandLens,
}: LensSettingsSortableListProps) => {
    const [draggingLensId, setDraggingLensId] = useState<number | null>(null);
    const [draggingOverLensId, setDraggingOverLensId] = useState<number | null>(null);

    const orderedLenses = sortLensesByDisplayOrder(displayLenses ?? lenses);

    const clearDragState = () => {
        setDraggingLensId(null);
        setDraggingOverLensId(null);
    };

    const onDragStart = (event: DragEvent<HTMLDivElement>, lens: ScenarioViewLens) => {
        if (!sortable || !lens.is_selected) {
            return;
        }
        event.stopPropagation();
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(lens.lens_id));
        setDraggingLensId(lens.lens_id);
        setDraggingOverLensId(lens.lens_id);
    };

    const onDragOver = (event: DragEvent<HTMLDivElement>, lens: ScenarioViewLens) => {
        if (!sortable || !lens.is_selected || draggingLensId == null) {
            return;
        }
        event.preventDefault();
        if (draggingOverLensId !== lens.lens_id) {
            setDraggingOverLensId(lens.lens_id);
        }
    };

    const onDrop = (event: DragEvent<HTMLDivElement>, targetLens: ScenarioViewLens) => {
        if (!sortable || !targetLens.is_selected) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const draggedLensId = Number(event.dataTransfer.getData('text/plain')) || draggingLensId;
        if (!draggedLensId || draggedLensId === targetLens.lens_id) {
            clearDragState();
            return;
        }

        onChange(reorderLensList(lenses, draggedLensId, targetLens.lens_id));
        clearDragState();
    };

    const onDragEnd = () => {
        clearDragState();
    };

    return (
        <StyledLensSettingsList>
            {orderedLenses.map((lens) => {
                const isDropTarget = draggingOverLensId === lens.lens_id && draggingLensId !== lens.lens_id;
                const isExpanded = expandedLensId === lens.lens_id;
                const canDrag = sortable && lens.is_selected;
                return (
                    <StyledLensSettingsItem
                        key={lens.lens_id}
                        draggable={canDrag}
                        isDragging={draggingLensId === lens.lens_id}
                        isDropTarget={isDropTarget}
                        isExpanded={isExpanded}
                        isSelected={lens.is_selected}
                        onDragStart={(event) => onDragStart(event, lens)}
                        onDragOver={(event) => onDragOver(event, lens)}
                        onDrop={(event) => onDrop(event, lens)}
                        onDragEnd={onDragEnd}
                        onClick={() => onExpandLens?.(lens.lens_id)}
                    >
                        <span className="lens-item-main">
                            {showDragHandle && lens.is_selected ? (
                                <HolderOutlined
                                    className="lens-item-drag-handle"
                                    onClick={(event) => event.stopPropagation()}
                                />
                            ) : null}
                            <span className="lens-item-label-group">
                                {lens.is_private ? (
                                    <span className="custom-lens-user-icon" aria-hidden>
                                        <User
                                            size={12}
                                            color={
                                                lens.is_selected
                                                    ? colorConstants.violet
                                                    : colorConstants.blackTransparent88
                                            }
                                        />
                                    </span>
                                ) : null}
                                <span className="lens-label">{lens.lens_name}</span>
                            </span>
                        </span>
                        <StyledLensExpandButton
                            type="button"
                            isExpanded={isExpanded}
                            aria-label={`Configure ${lens.lens_name}`}
                            aria-expanded={isExpanded}
                        >
                            <ChevronRight />
                        </StyledLensExpandButton>
                    </StyledLensSettingsItem>
                );
            })}
        </StyledLensSettingsList>
    );
};

export default LensSettingsSortableList;
